import 'animate.css';

import { CircularProgress, Grid } from '@mui/material';
import { RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { validateEmail } from '@nx-smartmonkey/shared/helpers';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as routes from '../../../constants/Routing';
import { useCreateSession } from '../../../hooks/session/useCreateSession';
import Button from '../../../react-components/Button';
import InputText from '../../../react-components/InputText';
import { PasswordInput } from '../../../react-components/PasswordInput';
import Text from '../../../react-components/Text';
import { AuthBody, AuthBodyFooter } from '../Auth.styled';
import { useAuthStatus } from './hooks/useAuthStatus';

interface LoginFormProps {
  setNotValidatedEmail: (email: string) => void;
}

export const LoginForm = ({ setNotValidatedEmail }: LoginFormProps) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState<string>(``);
  const [password, setPassword] = useState<string>(``);

  const {
    success,
    isLoading,
    notValidatedEmail,
    hasTwoFactorMethods,
    phone,
    run: login,
    reset,
  } = useCreateSession({
    email,
    password,
  });

  const { onLoginIntentSuccess } = useAuthStatus();

  useEffect(() => {
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (success) {
      if (hasTwoFactorMethods) onLoginIntentSuccess({ email, password, phone });
      else {
        const redirectTo = searchParams.get(`redirectTo`);
        if ((redirectTo ?? ``) !== ``) {
          // TODO: Control if URL is from highway??
          navigate(redirectTo as string);
        } else {
          navigate(routes.PRIVATE_HIGHWAY);
        }
      }
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success]);

  useEffect(() => {
    if (!isLoading && notValidatedEmail) {
      setNotValidatedEmail(notValidatedEmail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notValidatedEmail]);

  const handleLogin = async () => {
    if ((email ?? ``) === `` && (password ?? ``) === ``) {
      return;
    }

    login();
  };

  return (
    <>
      <AuthBody container direction="column" justifyContent="center">
        <Grid item className="animate__animated animate__fadeIn">
          <Grid container justifyContent="center">
            <Text variant="h5" intlId="auth.login" />
          </Grid>

          <VSpacer small />

          <Grid container direction="column" justifyContent="center" style={{ minHeight: `250px` }}>
            <Text variant="body" intlId="auth.login.email" />

            <VSpacer small />

            <InputText
              id="email"
              type="email"
              autoFocus
              placeholder={intl.formatMessage({ id: `none` })}
              fieldValue={email}
              onChange={(mail: any) => setEmail(mail)}
              size="medium"
              variant="outlined"
              onKeyDown={(ev) => {
                if (ev.keyCode === 13) {
                  handleLogin();
                }
              }}
              inputProps={{
                style: {
                  minHeight: `24px`,
                },
              }}
              required
              validators={[
                {
                  errorMessage: (
                    <Text
                      tagName="span"
                      style={{ color: RoutalPalette.danger }}
                      intlId="settings.error.invalid_email"
                    />
                  ),
                  isInvalid: (text?: string) => !text || !validateEmail(text),
                },
              ]}
            />

            <VSpacer medium />

            <Text variant="body" intlId="auth.login.password" />

            <VSpacer small />

            <PasswordInput
              id="password"
              type="password"
              placeholder={intl.formatMessage({ id: `none` })}
              fieldValue={password}
              onChange={(psw: any) => setPassword(psw)}
              size="medium"
              variant="outlined"
              required
              showHints={false}
              onKeyDown={(ev) => {
                if (ev.keyCode === 13) {
                  handleLogin();
                }
              }}
              inputProps={{
                style: {
                  minHeight: `24px`,
                },
              }}
            />

            <VSpacer large />

            {isLoading ? (
              <Grid container justifyContent="center">
                <CircularProgress />
              </Grid>
            ) : (
              <Grid item container justifyContent="center">
                <Button
                  id="login_submit"
                  buttonType="button"
                  type="submit"
                  intlId="auth.login"
                  size="large"
                  onClick={handleLogin}
                  style={{
                    width: `100%`,
                  }}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
      </AuthBody>

      <AuthBodyFooter item container justifyContent="space-between">
        <Grid item>
          <Text
            isBold
            style={{
              fontSize: `12px`,
            }}
            variant="button"
            intlId="auth.login.register"
            onClick={() => navigate(routes.AUTH.REGISTER)}
          />
        </Grid>

        <Grid item>
          <Text
            isBold
            style={{
              fontSize: `12px`,
            }}
            variant="button"
            intlId="auth.forgotten_password"
            onClick={() => navigate(routes.AUTH.RESET_PASSWORD)}
          />
        </Grid>
      </AuthBodyFooter>
    </>
  );
};
